/**
 * PageShell Component
 * Chassi único de toda página do sistema: largura, espaçamento, breadcrumb,
 * título, subtítulo e ações do topo.
 *
 * Existe para que nenhuma página precise decidir `Container`, `maxWidth` ou
 * padding por conta própria — foi essa decisão solta que gerou 4 variantes de
 * espaçamento e 3 de largura no sistema. O ESLint bloqueia `Container` e
 * `maxWidth` dentro de `src/pages/**`; use este componente.
 *
 * **Largura:** full-bleed, com o mesmo padding lateral do `Toolbar` da AppBar
 * (`2` no mobile, `3` a partir de `sm`). Assim o título da página nasce alinhado
 * com o logo do menu em qualquer largura de tela. Antes o conteúdo ficava num
 * `Container maxWidth="xl"` centralizado, e acima de ~1584px a sobra se dividia
 * dos dois lados — em 1908px o conteúdo começava 182px à direita do menu, e a
 * 90% de zoom, 288px.
 *
 * @example
 * <PageShell
 *   breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Empresas' }]}
 *   title="Gestão de Empresas"
 *   subtitle="Gerencie as empresas cadastradas no sistema"
 *   actions={<Button variant="contained">Nova Empresa</Button>}
 * >
 *   <DataTable ... />
 * </PageShell>
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import PageLayout from '../layout/PageLayout';
import { PageBreadcrumb, type BreadcrumbItem } from './PageBreadcrumb';

/** Mesmo padding lateral do `Toolbar` do MUI, para alinhar conteúdo e menu. */
const GUTTER = { xs: 2, sm: 3 };

export interface PageShellProps {
  /** Trilha de navegação. O primeiro item costuma ser `{ label: 'Início', path: '/' }`. */
  breadcrumbs: BreadcrumbItem[];
  /**
   * Título da página (h1). Obrigatório em listagem e formulário.
   *
   * Omita nas telas de VISUALIZAÇÃO: ali quem identifica o registro é o `name`
   * do `IdentityBanner`, e um `h1` acima dele só repetia a informação. Sem
   * título, o cabeçalho fica com o breadcrumb e as ações alinhadas à direita.
   */
  title?: string;
  /** Linha de apoio sob o título. */
  subtitle?: string;
  /** Botões do canto superior direito. Agrupados e quebram linha sozinhos. */
  actions?: React.ReactNode;
  /** Renderiza sem o PageLayout (AppBar/Footer). Use só em página pública/login. */
  bare?: boolean;
  children: React.ReactNode;
}

export const PageShell: React.FC<PageShellProps> = ({
  breadcrumbs,
  title,
  subtitle,
  actions,
  bare = false,
  children,
}) => {
  const temTitulo = Boolean(title || subtitle);
  const grupoDeAcoes = actions && (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>{actions}</Box>
  );

  const header = (
    <Box sx={{ mb: 3 }}>
      {/*
        Com título (listagem, formulário): breadcrumb em cima, e embaixo a linha
        do título com as ações à direita.

        Sem título (visualização): breadcrumb e ações dividem a MESMA linha —
        breadcrumb à esquerda, ações à direita. Sem isso a barra de ações ficava
        sozinha numa faixa própria, empurrando o conteúdo para baixo à toa.
      */}
      {temTitulo ? (
        <>
          <PageBreadcrumb items={breadcrumbs} />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box>
              {title && (
                <Typography variant="h4" component="h1">
                  {title}
                </Typography>
              )}
              {subtitle && (
                <Typography variant="body1" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
            </Box>
            {grupoDeAcoes}
          </Box>
        </>
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          {/* `mb={0}`: a margem padrão do breadcrumb desalinharia a linha. */}
          <PageBreadcrumb items={breadcrumbs} mb={0} />
          {grupoDeAcoes}
        </Box>
      )}
    </Box>
  );

  const content = (
    <Box sx={{ px: GUTTER, py: 3 }}>
      {header}
      {children}
    </Box>
  );

  return bare ? content : <PageLayout>{content}</PageLayout>;
};

export default PageShell;
